export type Locale = 'en' | 'fr';
export type Currency = 'Franc CFA';

interface LanguageConfigInterface {
  units: string[];
  teens: string[];
  tens: string[];
  scales: string[];
  specialCases: { [key: number]: string };
  zeroWord: string;
  currencyPluralization: (currency: Currency, amount: number) => string;
  fractionalUnit: (currency: Currency, amount: number) => string;
  decimalConnector: string;
}

export abstract class LanguageConfig implements LanguageConfigInterface {
  units: string[] = [];
  teens: string[] = [];
  tens: string[] = [];
  scales: string[] = [];
  specialCases: { [key: number]: string } = {};
  zeroWord = '';
  currencyPluralization: (currency: Currency, amount: number) => string = (
    currency,
    amount,
  ) => (amount > 1 ? currency + 's' : currency);
  fractionalUnit: (currency: Currency, amount: number) => string = (
    currency,
    amount,
  ) => (amount > 1 ? 'Cents' : 'Cent');
  decimalConnector = 'and';

  numberToWords(num: number): string {
    if (num === 0) return this.zeroWord;

    const words: string[] = [];
    const chunks: number[] = [];
    while (num > 0) {
      chunks.push(num % 1000);
      num = Math.floor(num / 1000);
    }

    for (let i = 0; i < chunks.length; i++) {
      const chunk = chunks[i];
      if (chunk === 0) continue;

      const chunkWords: string[] = [];
      const hundreds = Math.floor(chunk / 100);
      const remainder = chunk % 100;

      if (this.specialCases[chunk]) {
        chunkWords.push(this.specialCases[chunk]);
      } else {
        if (hundreds > 0) {
          chunkWords.push(this.getHundreds(hundreds));
        }
        if (remainder > 0) {
          chunkWords.push(this.getTensAndUnits(remainder));
        }
      }

      if (i > 0 && chunkWords.length > 0) {
        chunkWords.push(this.scales[i]);
      }

      words.unshift(chunkWords.join(' ').trim());
    }

    return words.join(' ').trim();
  }

  protected abstract getHundreds(hundreds: number): string;

  protected getTensAndUnits(remainder: number): string {
    if (this.specialCases[remainder]) {
      return this.specialCases[remainder];
    }
    if (remainder < 10) {
      return this.units[remainder];
    }
    if (remainder < 20) {
      return this.teens[remainder - 10];
    }
    const ten = Math.floor(remainder / 10);
    const unit = remainder % 10;
    return unit > 0 ? `${this.tens[ten]} ${this.units[unit]}` : this.tens[ten];
  }

  currencyToWords(amount: number, currency: Currency): string {
    const integerPart = Math.floor(amount);
    const decimalPart = Math.round((amount - integerPart) * 100);

    let result = this.numberToWords(integerPart);
    result += ` ${this.currencyPluralization(currency, integerPart)}`;

    if (decimalPart > 0) {
      const decimalWords = this.numberToWords(decimalPart);
      const fractionalName = this.fractionalUnit(currency, decimalPart);
      result += ` ${this.decimalConnector} ${decimalWords} ${fractionalName}`;
    }

    return result;
  }
}
